import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiClient, api } from './index';
import type { UserInfo, FamilySimpleInfo, LoginResponse } from './types';

interface AuthContextType {
  isAuthenticated: boolean;
  user: UserInfo | null;
  families: FamilySimpleInfo[];
  currentFamily: FamilySimpleInfo | null;
  accessToken: string | null;
  login: (response: LoginResponse) => void;
  logout: () => void;
  setCurrentFamily: (family: FamilySimpleInfo) => void;
  refreshUser: () => Promise<void>;
  refreshFamilies: () => Promise<void>;
  updateCurrentFamilyName: (name: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<UserInfo | null>(null);
  const [families, setFamilies] = useState<FamilySimpleInfo[]>([]);
  const [currentFamily, setCurrentFamilyState] = useState<FamilySimpleInfo | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // 后台刷新家庭数据（获取最新的 exchange_ratio 等字段）
  const refreshFamiliesData = async () => {
    try {
      const response = await api.family.getMyFamilies();
      if (response.code === 0 && response.data) {
        setFamilies(response.data);
        localStorage.setItem('families', JSON.stringify(response.data));
        // 更新当前家庭
        const savedCurrentFamily = localStorage.getItem('currentFamily');
        if (savedCurrentFamily) {
          const currentId = JSON.parse(savedCurrentFamily).id;
          const updated = response.data.find((f: any) => f.id === currentId);
          if (updated) {
            setCurrentFamilyState(updated);
            localStorage.setItem('currentFamily', JSON.stringify(updated));
          }
        }
      }
    } catch (error) {
      // 静默失败，不影响用户体验
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const savedUser = localStorage.getItem('user');
    const savedFamilies = localStorage.getItem('families');
    const savedCurrentFamily = localStorage.getItem('currentFamily');

    if (token) {
      setAccessToken(token);
      apiClient.setToken(token);
      setIsAuthenticated(true);

      if (savedUser) {
        setUser(JSON.parse(savedUser));
      }
      if (savedFamilies) {
        setFamilies(JSON.parse(savedFamilies));
      }
      if (savedCurrentFamily) {
        setCurrentFamilyState(JSON.parse(savedCurrentFamily));
      }

      // 后台刷新家庭数据
      refreshFamiliesData();
    }
    setLoading(false);
  }, []);

  const login = (response: LoginResponse) => {
    const { access_token, refresh_token, user, families, current_family } = response;

    setAccessToken(access_token);
    apiClient.setToken(access_token);
    apiClient.setRefreshToken(refresh_token);

    setUser(user);
    setFamilies(families);
    setCurrentFamilyState(current_family || null);
    setIsAuthenticated(true);

    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('families', JSON.stringify(families));
    if (current_family) {
      localStorage.setItem('currentFamily', JSON.stringify(current_family));
    }
  };

  const logout = () => {
    setAccessToken(null);
    setUser(null);
    setFamilies([]);
    setCurrentFamilyState(null);
    setIsAuthenticated(false);

    apiClient.clearToken();
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('families');
    localStorage.removeItem('currentFamily');
  };

  const setCurrentFamily = (family: FamilySimpleInfo) => {
    setCurrentFamilyState(family);
    localStorage.setItem('currentFamily', JSON.stringify(family));
  };

  const refreshUser = async () => {
    try {
      const response = await api.auth.getCurrentUser();
      if (response.code === 0 && response.data) {
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const refreshFamilies = async () => {
    try {
      const response = await api.family.getMyFamilies();
      if (response.code === 0 && response.data) {
        setFamilies(response.data);
        localStorage.setItem('families', JSON.stringify(response.data));
        // 如果当前家庭在列表中，更新它
        if (currentFamily) {
          const updatedFamily = response.data.find((f: any) => f.id === currentFamily.id);
          if (updatedFamily) {
            setCurrentFamilyState(updatedFamily);
            localStorage.setItem('currentFamily', JSON.stringify(updatedFamily));
          }
        }
      }
    } catch (error) {
      console.error('Failed to refresh families:', error);
    }
  };

  const updateCurrentFamilyName = (name: string) => {
    if (currentFamily) {
      const updatedFamily = { ...currentFamily, name };
      setCurrentFamilyState(updatedFamily);
      localStorage.setItem('currentFamily', JSON.stringify(updatedFamily));

      // 同时更新 families 列表
      setFamilies(prev => {
        const updated = prev.map(f =>
          f.id === currentFamily.id ? { ...f, name } : f
        );
        // 保存更新后的families到localStorage
        localStorage.setItem('families', JSON.stringify(updated));
        return updated;
      });
    }
  };

  if (loading) {
    return null;
  }

  const contextValue: AuthContextType = {
    isAuthenticated,
    user,
    families,
    currentFamily,
    accessToken,
    login,
    logout,
    setCurrentFamily,
    refreshUser,
    refreshFamilies,
    updateCurrentFamilyName
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
